import React from "react";
import { Flex } from "../Flex";
import { Input } from "../Input";
import { Text } from "../Text";
import { EditIcon } from "../icons";
import { useToggle } from "../../hooks";

const PasswordInput = ({
  cell,
  autoFocus,
  onFocus,
  onKeyDown,
  ...rest
}: {
  cell: { value?: string; placeholder: string; type: string; name: string };
  autoFocus: boolean;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) => {
  const [type, setType] = React.useState(cell.type);
  return (
    <Flex align="center" sx={{ flex: 1, position: "relative" }}>
      <Input
        sx={{
          mr: 2,
          ":focus": {
            outline: "none",
          },
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        autoComplete="new-password"
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        type={type}
        name={cell.name}
        defaultValue={cell.value}
        placeholder={cell.placeholder}
        {...rest}
      />
      <Text
        as="button"
        fontSize={0}
        sx={{
          position: "absolute",
          top: 0,
          right: 4,
          height: "100%",
          display: "flex",
          alignItems: "center",
          color: "primary",
          cursor: "pointer",
          appearance: "none",
          border: 0,
          bg: "transparent",
          ":focus": {
            outline: "none",
          },
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (type == "password") {
            setType("text");
          }
          if (type === "text") {
            setType("password");
          }
        }}
      >
        {type === "password" ? "Show" : "Hide"}
      </Text>
    </Flex>
  );
};

// TODO: (at) refactor a more robust multi cell solution
const EditableCells: React.FunctionComponent<{
  cell: { value: string; placeholder: string; type: string; name: string };
  onEdit: (newValues: { [key: string]: string }) => void;
  iconHover?: boolean;
  open?: boolean;
  shouldFocus?: boolean;
}> = ({ cell, onEdit, iconHover, open, shouldFocus }) => {
  const { isOpen, onOpen, onClose } = useToggle(open);

  React.useEffect(() => {
    if (open && !isOpen) {
      onOpen();
    }
  }, [open, isOpen]);

  const onBlur = async (e: React.FocusEvent<HTMLDivElement>) => {
    e.preventDefault();
    const currentTarget = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      const { name, value } = currentTarget.querySelector(
        "input",
      ) as HTMLInputElement;
      await onEdit({
        [name]: value,
      });
      onClose();
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    e.currentTarget.select();

  const isNumericInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.keyCode;
    return (
      (key >= 48 && key <= 57) || // Allow number line
      (key >= 96 && key <= 105) // Allow number pad
    );
  };

  const isModifierKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.keyCode;
    return (
      event.shiftKey === true ||
      key === 35 ||
      key === 36 || // Allow Shift, Home, End
      key === 8 ||
      key === 9 ||
      key === 13 ||
      key === 46 || // Allow Backspace, Tab, Enter, Delete
      (key > 36 && key < 41) || // Allow left, up, right, down
      // Allow Ctrl/Command + A,C,V,X,Z
      ((event.ctrlKey === true || event.metaKey === true) &&
        (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
    );
  };

  const enforceFormat = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Input must be of a valid number format or a modifier key, and not longer than ten digits
    if (!isNumericInput(event) && !isModifierKey(event)) {
      event.preventDefault();
    }
  };

  const formatToPhone = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isModifierKey(event)) {
      return;
    }

    const target = event.target as HTMLTextAreaElement;
    const input = target.value.replace(/\D/g, "").substring(0, 10); // First ten digits of input only
    const zip = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      target.value = `${zip}-${middle}-${last}`;
    } else if (input.length > 3) {
      target.value = `${zip}-${middle}`;
    } else if (input.length > 0) {
      target.value = `${zip}`;
    }
  };

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (cell.type === "tel") {
      enforceFormat(e);
    }
    if (e.keyCode === 13) {
      e.preventDefault();
      const { name, value } = e.currentTarget;
      await onEdit({
        [name]: value,
      });
      if (isOpen) {
        return onClose();
      }
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) =>
    cell.type === "tel" && formatToPhone(e);

  return isOpen ? (
    <Flex onBlur={onBlur}>
      <form autoComplete="new-password">
        {cell.type === "password" ? (
          <PasswordInput
            key={cell.name}
            cell={cell}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={shouldFocus === false ? false : true}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
          />
        ) : (
          <Input
            key={cell.name}
            sx={{
              mr: 2,
              ":focus": {
                outline: "none",
              },
            }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={shouldFocus === false ? false : true}
            autoComplete="new-password"
            onFocus={onFocus}
            name={cell.name}
            type={cell.type}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            defaultValue={cell.value}
            placeholder={cell.placeholder}
          />
        )}
      </form>
    </Flex>
  ) : (
    <Flex
      align="center"
      sx={{
        cursor: "pointer",
        pl: 3,
        ...(iconHover && {
          ":hover": {
            color: "primary",
            textDecoration: "underline",
            svg: { display: "block" },
          },
        }),
      }}
      onClick={onOpen}
    >
      <Text
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        password={cell.type === "password"}
      >
        {cell.value || "------"}
      </Text>
      {iconHover && (
        <EditIcon
          sx={{
            display: "none",
          }}
          size="16px"
        />
      )}
    </Flex>
  );
};

export default EditableCells;
